// @ts-nocheck
import React, { useEffect, useState } from 'react';
import './CustomTabs.css';
import * as PropTypes from 'prop-types';

/**
 * Functional React component for rendering a set of custom tabs.
 *
 * @param {Object} props - The component's properties.
 * @param {ReactNode|ReactNode[]} props.children - The tab components to be displayed.
 * @param {function} [props.handleTabChange] - Callback function to handle tab change.
 * @param {number} [props.activeIndex=0] - Index of the initially active tab.
 * @param {number} [props.requestedIndex] - A tab explicitly asked for after the first render (an in-page link to
 *   `?tab=`). The active tab follows it; while it is undefined the tabs are left alone.
 * @param {string} [props.requestKey] - Changes on every navigation, so asking again for the tab that is already
 *   requested (after the operator clicked another one) still switches to it.
 * @param {string} [props.display=''] - Additional CSS classes for styling the tab buttons.
 * @param {...any} props.rest - Additional props to be spread on the root element.
 *
 * @returns {JSX.Element} React element representing the custom tabs.
 */

const CustomTabs = ({
  children,
  handleTabChange,
  activeIndex = 0,
  requestedIndex,
  requestKey,
  display = '',
  ...rest
}) => {
  const tabs = React.Children.toArray(children);
  const [activeTab, setActiveTab] = useState(activeIndex);

  // Manual tab clicks change neither prop, so they are never overridden.
  useEffect(() => {
    if (requestedIndex !== undefined) {
      setActiveTab(requestedIndex);
    }
  }, [requestedIndex, requestKey]);

  const handleTabClick = (index) => {
    setActiveTab(index);
    if (typeof handleTabChange === 'function') {
      handleTabChange(index);
    }
  };

  return (
    <div {...rest}>
      <div className={`tab-buttons ${display}`}>
        {tabs?.map((tab, index) => (
          <button
            key={index}
            className={activeTab === index ? 'active' : ''}
            onClick={() => handleTabClick(index)}
          >
            {tab?.props?.label ?? `Tab ${index + 1}`}
          </button>
        ))}
      </div>
      <div className='tab-content'>{tabs[activeTab] || null}</div>
    </div>
  );
};

CustomTabs.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node)
  ]),
  handleTabChange: PropTypes.func,
  activeIndex: PropTypes.number,
  requestedIndex: PropTypes.number,
  requestKey: PropTypes.string,
  display: PropTypes.string
};

export default CustomTabs;
